import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios, { useApiRequest } from "../../api/axios";
import { ERROR_SNACKBAR, PARTS_ENDPOINT, SUCCESS_SNACKBAR } from "../../consts";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import {
  DECIMAL_TWO_PLACES_INPUT_REGEX,
  formatDecimalForDisplay,
  formatDecimalForInput,
  formatMoney,
  toApiDecimal,
} from "../../utils";

/** Which of the part's orders the new price lands on (PartPriceChangeScope). */
type Scope = "current_order" | "newest_order" | "not_used_orders";

type PriceChangeOrder = {
  ordered_part_id: number;
  number: string | null;
  status: string;
  status_display: string;
  price_net: string;
  /** Something was already used from it - those uses get repriced too. */
  is_partly_used: boolean;
};

/** What `parts/{id}/change-price/` offers, read through the same selection the change applies. */
type PriceChangeOptions = {
  /** The order the current price comes from - the one the next use is drawn from. */
  current_order: PriceChangeOrder | null;
  /** The latest order not used up yet, possibly still on its way. */
  newest_order: PriceChangeOrder | null;
  not_used_orders_count: number;
  not_used_orders_partly_used: boolean;
};

const extractErrorMessage = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    if (typeof first === "object" && first && "message" in first) {
      return (first as { message?: string }).message ?? null;
    }
  }
  if (typeof value === "object" && value && "message" in value) {
    return (value as { message?: string }).message ?? null;
  }
  return null;
};

const buildChangePriceUrl = (partId: number | string) => `${PARTS_ENDPOINT}${partId}/change-price/`;

/** An order as one caption line: number, status and its price today. */
const describeOrder = (order: PriceChangeOrder) =>
  `${order.number ?? "-"} · ${order.status_display} · ${formatMoney(order.price_net)}`;

interface PartPriceChangeFormProps {
  partId: number | string;
  onSuccess?: () => void;
}

/**
 * Sets a part's net purchase price on its newest order, or on every order not
 * used yet. VAT, quantities and statuses of the orders stay as they are.
 */
export default function PartPriceChangeForm({ partId, onSuccess }: PartPriceChangeFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, executeRequest } = useApiRequest();
  const [options, setOptions] = useState<PriceChangeOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [priceNet, setPriceNet] = useState("");
  const [isPriceFocused, setIsPriceFocused] = useState(false);
  const [scope, setScope] = useState<Scope>("current_order");
  const [serverErrors, setServerErrors] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const response = await executeRequest(() => axios.get(buildChangePriceUrl(partId)));
      if (cancelled) return;
      if (response?.success) {
        const data: PriceChangeOptions = response.data;
        setOptions(data);
        // Start from what the part costs today, so a small change is a small edit.
        setPriceNet(formatDecimalForInput((data.current_order ?? data.newest_order)?.price_net));
        // Nothing in stock means no current order - only the newest is left to pick.
        if (!data.current_order) setScope("newest_order");
      } else {
        setLoadError(
          extractErrorMessage(response?.errorData?.detail) ||
            extractErrorMessage(response?.errorData) ||
            t("There was an error, please review the form.")
        );
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [executeRequest, partId, t]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    if (nextValue === "" || DECIMAL_TWO_PLACES_INPUT_REGEX.test(nextValue)) {
      setPriceNet(nextValue);
    }
  };

  const handleSubmit = async () => {
    setServerErrors(null);
    const response = await executeRequest(() =>
      axios.post(buildChangePriceUrl(partId), {
        price_net: priceNet ? toApiDecimal(priceNet) : null,
        scope,
      })
    );

    if (response?.success) {
      enqueueSnackbar(t("Part price changed."), SUCCESS_SNACKBAR);
      onSuccess?.();
      return;
    }

    setServerErrors(response?.errorData);
    enqueueSnackbar(
      extractErrorMessage(response?.errorData?.message) ||
        extractErrorMessage(response?.errorData?.detail) ||
        t("There was an error, please review the form."),
      ERROR_SNACKBAR
    );
  };

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!options) return <CustomCircularProgress />;

  const { current_order: currentOrder, newest_order: newestOrder } = options;
  // With a single open order the newest one is the current one - offering it
  // twice would only make the two choices look different when they are not.
  const showsNewestOrder =
    newestOrder !== null && newestOrder.ordered_part_id !== currentOrder?.ordered_part_id;
  const repricesUsedStock = {
    current_order: Boolean(currentOrder?.is_partly_used),
    newest_order: Boolean(newestOrder?.is_partly_used),
    not_used_orders: options.not_used_orders_partly_used,
  }[scope];
  const priceError = extractErrorMessage(serverErrors?.price_net);
  const scopeError = extractErrorMessage(serverErrors?.scope);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t("Current Price")}
        </Typography>
        {currentOrder ? (
          <Typography variant="body1">
            {`${formatMoney(currentOrder.price_net)} ${t("Net")} · ${t("From Order")} ${currentOrder.number ?? "-"}`}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("No delivered order with stock left")}
          </Typography>
        )}
      </Box>

      <TextField
        size="small"
        label={t("New price net")}
        value={isPriceFocused ? priceNet : formatDecimalForDisplay(priceNet)}
        onChange={handlePriceChange}
        onFocus={() => setIsPriceFocused(true)}
        onBlur={() => setIsPriceFocused(false)}
        // The idle value carries grouping spaces, so the pattern has to admit them.
        inputProps={{ inputMode: "decimal", pattern: "[0-9\\s  ]*[.,]?[0-9]*" }}
        InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
        required
        fullWidth
        error={Boolean(priceError)}
        helperText={priceError || t("VAT of each order stays as it is.")}
      />

      <FormControl error={Boolean(scopeError)}>
        {/* A heading, not an input: it should not turn blue while a radio has focus. */}
        <FormLabel focused={false}>{t("Apply to")}</FormLabel>
        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
          {currentOrder && (
            <FormControlLabel
              value="current_order"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">{t("Current order")}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {describeOrder(currentOrder)}
                  </Typography>
                </Box>
              }
            />
          )}
          {showsNewestOrder && newestOrder && (
            <FormControlLabel
              value="newest_order"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2">{t("Newest order")}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {describeOrder(newestOrder)}
                  </Typography>
                </Box>
              }
            />
          )}
          <FormControlLabel
            value="not_used_orders"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">{t("All orders not used yet")}</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {t("Ordered and delivered orders: {{count}}", { count: options.not_used_orders_count })}
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
        {scopeError && <FormHelperText>{scopeError}</FormHelperText>}
      </FormControl>

      {repricesUsedStock && (
        <Alert
          severity="warning"
          sx={{
            alignItems: "center",
            // A larger icon for a two-line message; the modal paints every icon
            // in the text colour, so the warning one gets its own back.
            "& .MuiAlert-icon": { fontSize: 30, py: 0 },
            "& .MuiAlert-icon .MuiSvgIcon-root": { color: "warning.main" },
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            {t("Some of this stock is already used.")}
          </Box>{" "}
          {t("Those uses get the new price too: on job invoices created from now on, in profitability and in statistics.")}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" loading={loading} onClick={handleSubmit}>
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
